import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

// Define the structure for campaign metrics
export interface CampaignMetrics {
  clicks: number[];
  spend: number[];
  sales: number[];
  cpc: number[];
  acos: number[];
}

// Define the complete data structure for the dashboard
export interface CampaignTrendData {
  dataSummary: CampaignMetrics; // Aggregated from all campaigns
  globalMaxBid: {
    exact: CampaignMetrics;
    tosExact: CampaignMetrics;
    rosExact: CampaignMetrics;
    ppExact: CampaignMetrics;
    pta: CampaignMetrics;
    tosPta: CampaignMetrics;
    rosPta: CampaignMetrics;
    ppPta: CampaignMetrics;
    broad: CampaignMetrics;
    cta: CampaignMetrics;
  };
  fullAuto: {
    closeMatch: CampaignMetrics;
    complements: CampaignMetrics;
    looseMatch: CampaignMetrics;
    substitutes: CampaignMetrics;
  };
  caAuto: {
    closeMatch: CampaignMetrics;
    complements: CampaignMetrics;
    looseMatch: CampaignMetrics;
    substitutes: CampaignMetrics;
  };
  microAuto: {
    closeMatch: CampaignMetrics;
    complements: CampaignMetrics;
    looseMatch: CampaignMetrics;
    substitutes: CampaignMetrics;
  };
}

// Helper function to create empty metrics
const createEmptyMetrics = (): CampaignMetrics => ({
  clicks: [0, 0, 0, 0],
  spend: [0, 0, 0, 0],
  sales: [0, 0, 0, 0],
  cpc: [0, 0, 0, 0],
  acos: [0, 0, 0, 0],
});

// Helper function to fetch metric data from Firestore
const fetchMetricData = async (
  companyName: string,
  asin: string,
  metricName: string
): Promise<number[]> => {
  try {
    const docRef = doc(db, 'pmt2_campaignresults', companyName, metricName, 'data');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const asinData = data.asinData?.[asin];
      if (asinData && Array.isArray(asinData) && asinData.length === 4) {
        return asinData.map(val => Number(val) || 0);
      }
    }
    return [0, 0, 0, 0];
  } catch (error) {
    console.error(`Error fetching ${metricName} for ${asin}:`, error);
    return [0, 0, 0, 0];
  }
};

// Helper function to fetch all metrics for a campaign type
const fetchCampaignMetrics = async (
  companyName: string,
  asin: string,
  campaignPrefix: string
): Promise<CampaignMetrics> => {
  const [clicks, spend, sales, cpc, acos] = await Promise.all([
    fetchMetricData(companyName, asin, `${campaignPrefix}Clicks`),
    fetchMetricData(companyName, asin, `${campaignPrefix}Spend`),
    fetchMetricData(companyName, asin, `${campaignPrefix}Sales`),
    fetchMetricData(companyName, asin, `${campaignPrefix}Cpc`),
    fetchMetricData(companyName, asin, `${campaignPrefix}Acos`),
  ]);

  return { clicks, spend, sales, cpc, acos };
};

// Helper function to sum multiple metric arrays
const sumMetrics = (metricsArray: CampaignMetrics[]): CampaignMetrics => {
  const result = createEmptyMetrics();
  
  metricsArray.forEach(metrics => {
    result.clicks = result.clicks.map((val, i) => val + metrics.clicks[i]);
    result.spend = result.spend.map((val, i) => val + metrics.spend[i]);
    result.sales = result.sales.map((val, i) => val + metrics.sales[i]);
    result.cpc = result.cpc.map((val, i) => val + metrics.cpc[i]);
    result.acos = result.acos.map((val, i) => val + metrics.acos[i]);
  });
  
  return result;
};

export const useCampaignTrendData = (companyName?: string, asin?: string) => {
  const [data, setData] = useState<CampaignTrendData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyName || !asin) {
        setData(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch all campaign metrics in parallel
        const [
          // Global Max Bid campaigns
          exact,
          tosExact,
          rosExact,
          ppExact,
          pta,
          tosPta,
          rosPta,
          ppPta,
          broad,
          cta,
          // Full Auto campaigns
          fullAutoCloseMatch,
          fullAutoComplements,
          fullAutoLooseMatch,
          fullAutoSubstitutes,
          // CA Auto campaigns
          caAutoCloseMatch,
          caAutoComplements,
          caAutoLooseMatch,
          caAutoSubstitutes,
          // Micro Auto campaigns
          microAutoCloseMatch,
          microAutoComplements,
          microAutoLooseMatch,
          microAutoSubstitutes,
        ] = await Promise.all([
          // Global Max Bid
          fetchCampaignMetrics(companyName, asin, 'exact'),
          fetchCampaignMetrics(companyName, asin, 'tosExact'),
          fetchCampaignMetrics(companyName, asin, 'rosExact'),
          fetchCampaignMetrics(companyName, asin, 'ppExact'),
          fetchCampaignMetrics(companyName, asin, 'pta'),
          fetchCampaignMetrics(companyName, asin, 'tosPta'),
          fetchCampaignMetrics(companyName, asin, 'rosPta'),
          fetchCampaignMetrics(companyName, asin, 'ppPta'),
          fetchCampaignMetrics(companyName, asin, 'broad'),
          fetchCampaignMetrics(companyName, asin, 'cta'),
          // Full Auto
          fetchCampaignMetrics(companyName, asin, 'fullAutoCloseMatch'),
          fetchCampaignMetrics(companyName, asin, 'fullAutoComplements'),
          fetchCampaignMetrics(companyName, asin, 'fullAutoLooseMatch'),
          fetchCampaignMetrics(companyName, asin, 'fullAutoSubstitutes'),
          // CA Auto
          fetchCampaignMetrics(companyName, asin, 'caAutoCloseMatch'),
          fetchCampaignMetrics(companyName, asin, 'caAutoComplements'),
          fetchCampaignMetrics(companyName, asin, 'caAutoLooseMatch'),
          fetchCampaignMetrics(companyName, asin, 'caAutoSubstitutes'),
          // Micro Auto
          fetchCampaignMetrics(companyName, asin, 'microAutoCloseMatch'),
          fetchCampaignMetrics(companyName, asin, 'microAutoComplements'),
          fetchCampaignMetrics(companyName, asin, 'microAutoLooseMatch'),
          fetchCampaignMetrics(companyName, asin, 'microAutoSubstitutes'),
        ]);

        // Calculate the aggregated "Data: 7 Day Results" by summing all campaigns
        const dataSummary = sumMetrics([
          exact, tosExact, rosExact, ppExact,
          pta, tosPta, rosPta, ppPta,
          broad, cta,
          fullAutoCloseMatch, fullAutoComplements, fullAutoLooseMatch, fullAutoSubstitutes,
          caAutoCloseMatch, caAutoComplements, caAutoLooseMatch, caAutoSubstitutes,
          microAutoCloseMatch, microAutoComplements, microAutoLooseMatch, microAutoSubstitutes,
        ]);

        const campaignTrendData: CampaignTrendData = {
          dataSummary,
          globalMaxBid: {
            exact,
            tosExact,
            rosExact,
            ppExact,
            pta,
            tosPta,
            rosPta,
            ppPta,
            broad,
            cta,
          },
          fullAuto: {
            closeMatch: fullAutoCloseMatch,
            complements: fullAutoComplements,
            looseMatch: fullAutoLooseMatch,
            substitutes: fullAutoSubstitutes,
          },
          caAuto: {
            closeMatch: caAutoCloseMatch,
            complements: caAutoComplements,
            looseMatch: caAutoLooseMatch,
            substitutes: caAutoSubstitutes,
          },
          microAuto: {
            closeMatch: microAutoCloseMatch,
            complements: microAutoComplements,
            looseMatch: microAutoLooseMatch,
            substitutes: microAutoSubstitutes,
          },
        };

        setData(campaignTrendData);
      } catch (err) {
        console.error('Error fetching campaign trend data:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch campaign trend data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [companyName, asin]);

  return { data, isLoading, error };
};